import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  isNativeApp, nativeCreate, nativePlay, nativePause, nativeStop, nativeReconnect,
  nativePosition, nativeSetVolume,
} from "@/lib/nativeAudio";
import { api } from "@/lib/api";
import { ensureClientId } from "@/components/AudienceHeartbeat";

const AudioCtx = createContext(null);

export const useAudio = () => {
  const ctx = useContext(AudioCtx);
  if (!ctx) throw new Error("useAudio must be used within AudioProvider");
  return ctx;
};

const APP_TITLE = "Fala Itapira e Região";
// Live stream considered "stuck" when position hasn't advanced for this long.
const STALL_MS = 3000;
// Interval between automatic reconnect attempts.
const RECONNECT_EVERY_MS = 3000;
// Watchdog sampling period.
const WATCHDOG_MS = 1000;
const MAX_RECONNECTS = 400; // ~20 min of continuous retries before giving up

export const AudioProvider = ({ children }) => {
  const audioRef = useRef(null);
  const [current, setCurrent] = useState(null); // { title, subtitle, cover, src, isLive, program, host }
  const [isPlaying, setIsPlaying] = useState(false);
  const [stalled, setStalled] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);

  const currentRef = useRef(null);
  const engineRef = useRef("web"); // "web" | "native"
  const shouldPlayRef = useRef(false); // user intent: keep playing
  const reconnectTimerRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const lastPosRef = useRef(-1);
  const lastAdvanceAtRef = useRef(0);
  const wakeLockRef = useRef(null);

  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  const markHealthy = useCallback(() => {
    lastAdvanceAtRef.current = Date.now();
    reconnectCountRef.current = 0;
    setStalled(false);
    setReconnecting(false);
  }, []);

  // ---------- Reconnect (web + native) ----------
  const scheduleReconnect = useCallback((immediate = false) => {
    const track = currentRef.current;
    if (!track?.isLive || !shouldPlayRef.current) return;
    if (reconnectTimerRef.current) return;
    if (reconnectCountRef.current >= MAX_RECONNECTS) return;
    reconnectCountRef.current += 1;
    setReconnecting(true);
    reconnectTimerRef.current = setTimeout(async () => {
      reconnectTimerRef.current = null;
      if (!shouldPlayRef.current) return;
      lastAdvanceAtRef.current = Date.now(); // give the new connection a grace period
      try {
        if (engineRef.current === "native") {
          await nativeReconnect(track.src);
        } else {
          const a = audioRef.current;
          if (!a) return;
          const sep = track.src.includes("?") ? "&" : "?";
          a.src = `${track.src}${sep}_r=${Date.now()}`;
          a.load();
          await a.play();
        }
      } catch {
        // play() may be blocked; watchdog will retry
      }
    }, immediate ? 50 : reconnectCountRef.current > 5 ? RECONNECT_EVERY_MS * 3 : RECONNECT_EVERY_MS);
  }, []);

  // ---------- Watchdog: samples position, detects silence/stall/unsolicited pause ----------
  useEffect(() => {
    const tick = async () => {
      const track = currentRef.current;
      if (!track?.isLive || !shouldPlayRef.current) return;
      let pos = 0;
      let paused = false;
      let errored = false;
      try {
        if (engineRef.current === "native") {
          pos = await nativePosition();
        } else {
          const a = audioRef.current;
          if (!a) return;
          pos = a.currentTime || 0;
          paused = a.paused;
          errored = !!a.error;
        }
      } catch {
        errored = true;
      }
      if (pos > lastPosRef.current + 0.05) {
        lastPosRef.current = pos;
        markHealthy();
        return;
      }
      lastPosRef.current = pos;
      const stuckFor = Date.now() - lastAdvanceAtRef.current;
      if (errored || paused) {
        setStalled(true);
        scheduleReconnect(true);
      } else if (stuckFor > STALL_MS) {
        setStalled(true);
        scheduleReconnect();
      }
    };
    const timer = setInterval(tick, WATCHDOG_MS);
    const onVisible = () => document.visibilityState === "visible" && tick();
    const onOnline = () => tick();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, [markHealthy, scheduleReconnect]);

  // ---------- <audio> element listeners (web engine) ----------
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.currentTime || 0);
    const onDur = () => setDuration(isFinite(a.duration) ? a.duration : 0);
    const onPlay = () => {
      setIsPlaying(true);
      try { navigator.mediaSession.playbackState = "playing"; } catch {}
    };
    const onPause = () => {
      try { navigator.mediaSession.playbackState = "paused"; } catch {}
      // Unsolicited pause (OS/background/interruption) on a live stream → resume ASAP.
      if (currentRef.current?.isLive && shouldPlayRef.current) {
        setStalled(true);
        scheduleReconnect(true);
        return;
      }
      setIsPlaying(false);
    };
    const onEnd = () => {
      if (currentRef.current?.isLive && shouldPlayRef.current) scheduleReconnect(true);
      else setIsPlaying(false);
    };
    const onError = () => {
      if (currentRef.current?.isLive && shouldPlayRef.current) scheduleReconnect();
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onError);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onError);
    };
  }, [scheduleReconnect]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    if (engineRef.current === "native") nativeSetVolume(volume).catch(() => {});
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate;
  }, [rate]);

  // ---------- Media Session (lock screen / notification controls on web) ----------
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    if (!current) {
      ms.metadata = null;
      return;
    }
    const logo = `${window.location.origin}/icon-512.png`;
    const artwork = current.isLive || !current.cover
      ? [{ src: logo, sizes: "512x512", type: "image/png" }]
      : [{ src: current.cover, sizes: "512x512", type: "image/jpeg" }];
    try {
      ms.metadata = new window.MediaMetadata({
        title: current.isLive ? APP_TITLE : current.title || APP_TITLE,
        artist: current.isLive
          ? [current.program || current.title, current.host && `com ${current.host}`].filter(Boolean).join(" · ")
          : current.subtitle || APP_TITLE,
        album: current.isLive ? "Rádio ao vivo" : APP_TITLE,
        artwork,
      });
    } catch {}
  }, [current]);

  // ---------- Wake Lock only while audio is playing ----------
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let cancelled = false;
    const acquire = async () => {
      if (wakeLockRef.current) return;
      try {
        const wl = await navigator.wakeLock.request("screen");
        if (cancelled) return wl.release().catch(() => {});
        wakeLockRef.current = wl;
        wl.addEventListener("release", () => { wakeLockRef.current = null; });
      } catch {}
    };
    const release = () => {
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
    if (isPlaying) acquire(); else release();
    const onVisible = () => document.visibilityState === "visible" && isPlaying && acquire();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (!isPlaying) release();
    };
  }, [isPlaying]);

  // ---------- Audience heartbeat: only while the live radio is really playing ----------
  useEffect(() => {
    const track = currentRef.current;
    if (!isPlaying || !track?.isLive) return;
    const send = () => api.post("/audience/heartbeat", { session_id: ensureClientId(), channel: "radio" }).catch(() => {});
    send();
    const t = setInterval(send, 20000);
    return () => clearInterval(t);
  }, [isPlaying, current]);

  // ---------- Controls ----------
  const resume = useCallback(async () => {
    shouldPlayRef.current = true;
    lastAdvanceAtRef.current = Date.now();
    if (engineRef.current === "native") {
      await nativePlay().catch(() => scheduleReconnect(true));
    } else {
      await audioRef.current?.play().catch(() => setIsPlaying(false));
    }
  }, [scheduleReconnect]);

  const pause = useCallback(async () => {
    shouldPlayRef.current = false;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
    setReconnecting(false);
    setStalled(false);
    if (engineRef.current === "native") {
      await nativePause().catch(() => {});
      setIsPlaying(false);
    } else {
      audioRef.current?.pause();
    }
  }, []);

  const playTrack = useCallback(async (track) => {
    const a = audioRef.current;
    if (!a) return;
    shouldPlayRef.current = true;
    reconnectCountRef.current = 0;
    lastPosRef.current = -1;
    lastAdvanceAtRef.current = Date.now();
    setCurrent(track);
    const useNative = isNativeApp() && track.isLive;
    if (useNative) {
      a.pause();
      engineRef.current = "native";
      try {
        await nativeCreate(
          { src: track.src, program: track.program || track.title, host: track.host },
          {
            onStatus: (status) => {
              if (status === "playing") { shouldPlayRef.current = true; setIsPlaying(true); }
              else if (status === "paused") { shouldPlayRef.current = false; setIsPlaying(false); setReconnecting(false); }
              else if (status === "stopped" && shouldPlayRef.current) scheduleReconnect(true);
            },
            onEnd: () => shouldPlayRef.current && scheduleReconnect(true),
          }
        );
        await nativeSetVolume(volume).catch(() => {});
        await nativePlay();
        setIsPlaying(true);
      } catch (err) {
        console.warn("Native audio failed, falling back to web:", err);
        engineRef.current = "web";
        a.src = track.src;
        a.load();
        a.play().catch(() => setIsPlaying(false));
      }
      return;
    }
    if (engineRef.current === "native") await nativeStop().catch(() => {});
    engineRef.current = "web";
    if (currentRef.current?.src !== track.src || a.error) {
      a.src = track.src;
      a.load();
    }
    a.play().catch((err) => {
      console.warn("Audio play blocked or failed:", err);
      setIsPlaying(false);
    });
  }, [volume, scheduleReconnect]);

  const toggle = useCallback(() => {
    if (!currentRef.current) return;
    if (shouldPlayRef.current && isPlaying) pause(); else resume();
  }, [isPlaying, pause, resume]);

  const stop = useCallback(async () => {
    await pause();
    if (engineRef.current === "native") await nativeStop().catch(() => {});
    const a = audioRef.current;
    if (a) { a.pause(); a.removeAttribute("src"); a.load(); }
    engineRef.current = "web";
    setCurrent(null);
    setIsPlaying(false);
  }, [pause]);

  const seek = useCallback((seconds) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = seconds;
    setProgress(seconds);
  }, []);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.duration || 0), (a.currentTime || 0) + delta));
  }, []);

  // Media Session action handlers (registered once; use refs)
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    try {
      ms.setActionHandler("play", () => resume());
      ms.setActionHandler("pause", () => pause());
      ms.setActionHandler("stop", () => stop());
    } catch {}
  }, [resume, pause, stop]);

  return (
    <AudioCtx.Provider
      value={{
        current,
        isPlaying,
        // true only while audio is really advancing (drives the sound waves)
        isAudible: isPlaying && !stalled && !reconnecting,
        stalled,
        reconnecting,
        volume,
        progress,
        duration,
        rate,
        setVolume,
        setRate,
        playTrack,
        toggle,
        stop,
        seek,
        skip,
      }}
    >
      {/* DOM-mounted <audio>: required for iOS Safari; playsInline avoids fullscreen takeover. */}
      <audio ref={audioRef} preload="none" playsInline style={{ display: "none" }} aria-hidden="true" />
      {children}
    </AudioCtx.Provider>
  );
};
